/**
 * live-news-hls-streams.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * All HLS / m3u8-based live news sources.
 *
 * HOW TO UPDATE:
 *  - `url` : Direct .m3u8 playlist URL.
 *            Replace this when a CDN URL changes — no rebuild needed if the
 *            URL is static, but a container rebuild IS required if the file
 *            is baked into the Next.js bundle (i.e. these are static imports).
 *
 * NOTES:
 *  - Some HLS tokens expire (e.g. hellokijk.com MSNBC/CNBC tokens).
 *    Replace the full URL when they do.
 *  - Akamaized and CDN streams are generally stable long-term.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { NewsSource } from "./live-news-types"

// ── US ───────────────────────────────────────────────────────────────────────
// ── US ───────────────────────────────────────────────────────────────────────
export const usHlsSources: NewsSource[] = [
    {
        name: "FOX",
        url: "/api/stream/proxy?url=https://stream.livenewsplay.com:9555/hls/foxnewssd/index.m3u8?token=35cfc6e68200d9d6c008c669bfe72805&expires=1772393884&sig=8e5fca3849123cf64576ec2b90133050eedb1c847dbc6aae517a93fad18bcb5c",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=foxnews.com&sz=32",
        epgId: "FoxNewsChannel.us"
    },
    {
        name: "CNN",
        url: "/api/stream/proxy?url=https://turnerlive.warnermediacdn.com/hls/live/586495/cnngo/cnn_slate/VIDEO_0_3564000.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=cnn.com&sz=32",
        epgId: "CNN.us"
    },
    {
        name: "NBC",
        url: "/api/stream/proxy?url=https://xumo-drct-nbcnn-ir8ze.fast.nbcuni.com/live/master.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=nbc.com&sz=32",
        epgId: "NBCNewsNow.us"
    },
    {
        name: "ABC",
        url: "/api/stream/proxy?url=https://aegis-cloudfront-1.tubi.video/d6cbb0de-68e4-4f3b-82f9-bf5d526e0bde/index.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=abc.com&sz=32",
        epgId: "ABCNewsLive.us"
    },
    {
        name: "CBS",
        url: "/api/stream/proxy?url=https://cbsn-us.cbsnstream.cbsnews.com/out/v1/55a8648e8f134e82a470f83d562deeca/master_24.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=cbsnews.com&sz=32",
        epgId: "CBSNews.us"
    },
    {
        name: "MS Now",
        url: "/api/stream/proxy?url=http://104.255.88.155/msnbc/index.m3u8",
        type: "hls",
        region: "US",
        fallbackUrl: "/api/stream/proxy?url=http://41.205.93.154/MSNBC/index.m3u8",
        fallbackType: "hls",
        icon: "https://www.google.com/s2/favicons?domain=ms.now&sz=32",
        epgId: "MSNBC.us"
    },
    {
        name: "NewsMAX",
        url: "/api/stream/proxy?url=https://nmxlive.akamaized.net/hls/live/529965/Live_1/1.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=newsmax.com&sz=32",
        epgId: "408645"
    },
    {
        name: "News12",
        url: "/api/stream/proxy?url=https://aegis-cloudfront-1.tubi.video/97fbb992-c212-4164-8406-abb6f731e517/playlist.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=longisland.news12.com&sz=32",
    },
    {
        name: "Scripps",
        url: "/api/stream/proxy?url=https://content.uplynk.com/channel/4bb4901b934c4e029fd4c1abfc766c37.m3u8",
        type: "hls",
        region: "US",
        icon: "https://www.google.com/s2/favicons?domain=scrippsnews.com&sz=32",
        epgId: "464874"
    },
]

// ── Canada ───────────────────────────────────────────────────────────────────
export const canadaHlsSources: NewsSource[] = [

    {
        name: "Global News",
        url: "/api/stream/proxy?url=https://live.corusdigitaldev.com/groupd/live/49a91e7f-1023-430f-8d66-561055f3d0f7/live.isml/.m3u8",
        type: "hls",
        region: "Canada",
        icon: "https://www.google.com/s2/favicons?domain=globaltv.com&sz=32",
        epgId: "GlobalNews.ca"
    },
]

// ── Business ─────────────────────────────────────────────────────────────────
export const businessHlsSources: NewsSource[] = [
    {
        name: "Bloomberg TV",
        url: "/api/stream/proxy?url=https://bloomberg.com/media-manifest/streams/global.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
        epgId: "Bloomberg.us"
    },
    {
        name: "Bloomberg Plus",
        url: "/api/stream/proxy?url=https://bloomberg.com/media-manifest/streams/us.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
        epgId: "Bloomberg.us"
    },
    {
        name: "Fox Business",
        url: "/api/stream/proxy?url=http://41.205.93.154/FOXBUSINESS/tracks-v1a1/mono.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=foxbusiness.com&sz=32",
        epgId: "464766"
    },
    {
        name: "CNBC",
        url: "https://stream.livenewsplay.com:9443/hls/cnbc/cnbcsd.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "CNBC.us"
    },
    {
        name: "TD Ameritrade",
        url: "/api/stream/proxy?url=https://tdameritrade-amazon.amagi.tv/playlist.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=schwab.com&sz=32",
        epgId: "SchwabNetwork.us"
    },
    {
        name: "Yahoo Finance",
        url: "/api/stream/proxy?url=https://d1ewctnvcwvvvu.cloudfront.net/playlist.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=finance.yahoo.com&sz=32",
        epgId: "YahooFinance.us"
    },
    {
        name: "Cheddar",
        url: "/api/stream/proxy?url=https://livestream.chdrstatic.com/b93e5b0d43ea306310a379971e384964acbe4990ce193c0bd50078275a9a657d/cheddar-42620/cheddarweblive/cheddar/primary/2.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cheddar.com&sz=32",
        epgId: "464798"
    },
    {
        name: "CNBC Europe",
        url: "/api/stream/proxy?url=https://viamotionhsi.netplus.ch/live/eds/cnbc/browser-HLS8/cnbc.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "55740"
    },
    {
        name: "BFM Business",
        url: "/api/stream/proxy?url=https://live-cdn-stream-euw1.bfmb.bct.nextradiotv.com/master.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bfmtv.com&sz=32",
        epgId: "55723"
    },
    {
        name: "RBK",
        url: "/api/stream/proxy?url=https://online-video.rbc.ru/spb/rbctv_spb.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=rbc.ru&sz=32",
        epgId: "RBK_TV.ru"
    },
    {
        name: "CNBC Arabic",
        url: "/api/stream/proxy?url=https://cnbc-live.akamaized.net/cnbc/master.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "CNBCArabic.ae"
    },
    {
        name: "Bloomberg Turkey",
        url: "/api/stream/proxy?url=https://tv.ensonhaber.com/bloomberght/bloomberght.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
        epgId: "387746"
    },
    {
        name: "Bloomberg Saudi Arabia",
        url: "/api/stream/proxy?url=https://live-news.asharq.com/asharq.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
        epgId: "443224"
    },
    {
        name: "CNBC Asia",
        url: "/api/stream/proxy?url=https://liveh34.vtvprime.vn/hls/CNBC/03.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "369813"
    },
    {
        name: "NDTV Profit",
        url: "/api/stream/proxy?url=https://ndtvprofit.akamaized.net/hls/live/2107404/ndtvprofit/chunklist_4.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=ndtvprofit.com&sz=32",
        epgId: "NDTVProfitPrime.in"
    },
    {
        name: "CNBC-TV18",
        url: "/api/stream/proxy?url=https://n18syndication.akamaized.net/bpk-tv/CNBC_TV18_NW18_MOB/output01/CNBC_TV18_NW18_MOB-audio_98834_eng=98800-video=2293600.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbctv18.com&sz=32",
        epgId: "463900"
    },
    {
        name: "CNBC-TV18 Prime",
        url: "/api/stream/proxy?url=https://n18syndication.akamaized.net/bpk-tv/CNBC_Tv18_Prime_HD_NW18_MOB/output01/index.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbctv18.com&sz=32",
        epgId: "464160"
    },
    {
        name: "Z-Business",
        url: "/api/stream/proxy?url=https://dwby15d04agvq.cloudfront.net/index_5.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=zeebiz.com&sz=32",
        epgId: "464117"
    },
    {
        name: "CNBC Indonesia",
        url: "/api/stream/proxy?url=https://live.cnbcindonesia.com/livecnbc/smil:cnbctv.smil/master.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "411927"
    },
    {
        name: "Bloomberg Mongolia",
        url: "/api/stream/proxy?url=https://cdn4.skygo.mn/live/disk1/BloombergMon/HLSv3-FTA/BloombergMon.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
    },
    {
        name: "Bloomberg QuickTake",
        url: "/api/stream/proxy?url=https://www.bloomberg.com/media-manifest/streams/qt.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=bloomberg.com&sz=32",
    },
    {
        name: "CNBC Brazil",
        url: "/api/stream/proxy?url=https://f593663c.wurl.com/master/f36d25e7e52f1ba8d7e56eb859c636563214f541/TEctYnJfVGltZXNCcmFzaWxsaWNlbmNpYWRvZXhjbHVzaXZvQ05CQ19ITFM/playlist.m3u8",
        type: "hls",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
    },
]

// ── UK ───────────────────────────────────────────────────────────────────────
export const ukHlsSources: NewsSource[] = [
    {
        name: "BBC",
        url: "/api/stream/proxy?url=https://dash2.antik.sk/live/test_bbc_world/playlist.m3u8",
        type: "hls",
        region: "UK",
        fallbackUrl: "/api/stream/proxy?url=https://www.youtube.com/watch?v=PmuT49yiMA4",
        fallbackType: "youtube",
        icon: "https://www.google.com/s2/favicons?domain=bbc.com&sz=32",
        epgId: "BBCNews.uk"
    },
    {
        name: "SkyNews",
        url: "/api/stream/proxy?url=https://linear417-gb-hls1-prd-ak.cdn.skycdp.com/100e/Content/HLS_001_1080_30/Live/channel(skynews)/index_1080-30.m3u8",
        type: "hls",
        region: "UK",
        icon: "https://www.google.com/s2/favicons?domain=news.sky.com&sz=32",
        epgId: "SkyNews.uk"
    },
    {
        name: "GBN",
        url: "/api/stream/proxy?url=https://live-gbnews.simplestreamcdn.com/live5/gbnews/bitrate1.isml/manifest.m3u8",
        type: "hls",
        region: "UK",
        icon: "https://www.google.com/s2/favicons?domain=gbnews.com&sz=32",
        epgId: "GBNews.uk"
    },
]

// ── Europe ───────────────────────────────────────────────────────────────────
export const europeHlsSources: NewsSource[] = [
    {
        name: "DW",
        url: "/api/stream/proxy?url=https://dwamdstream102.akamaized.net/hls/live/2015525/dwstream102/index.m3u8",
        type: "hls",
        region: "Europe",
        icon: "https://www.google.com/s2/favicons?domain=dw.com&sz=32",
        epgId: "DWEnglish.de"
    },
    {
        name: "France 24",
        url: "/api/stream/proxy?url=https://live.france24.com/hls/live/2037218-b/F24_EN_HI_HLS/master_5000.m3u8",
        type: "hls",
        region: "Europe",
        icon: "https://www.google.com/s2/favicons?domain=france24.com&sz=32",
        epgId: "France24English.fr"
    },
    {
        name: "Euronews",
        url: "/api/stream/proxy?url=https://aegis-cloudfront-1.tubi.video/b1c4d439-03a5-4c40-ae9d-90a183e010c9/euronews-en.m3u8",
        type: "hls",
        region: "Europe",
        icon: "https://www.google.com/s2/favicons?domain=euronews.com&sz=32",
        epgId: "Euronews.uk"
    },
    {
        name: "RT",
        url: "https://rumble.com/live-hls-dvr/33aw1a/playlist.m3u8",
        type: "hls",
        region: "Europe",
        icon: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a0/Russia-today-logo.svg/330px-Russia-today-logo.svg.png",
    },
]

// ── Middle East ──────────────────────────────────────────────────────────────
export const middleEastHlsSources: NewsSource[] = [
    {
        name: "TRT World",
        url: "/api/stream/proxy?url=https://tv-trtworld.medya.trt.com.tr/master_1440.m3u8",
        type: "hls",
        region: "Middle East",
        icon: "https://www.google.com/s2/favicons?domain=trtworld.com&sz=32",
        epgId: "9621"
    },

    {
        name: "Al Jazeera",
        url: "/api/stream/proxy?url=https://live-hls-web-aje-gcp.thehlive.com/AJE/01.m3u8",
        type: "hls",
        region: "Middle East",
        icon: "https://www.google.com/s2/favicons?domain=aljazeera.com&sz=32",
        epgId: "AlJazeera.us"
    },
    {
        name: "PressTV",
        url: "/api/stream/proxy?url=https://live.presstv.ir/hls/presstv.m3u8",
        type: "hls",
        region: "Middle East",
        icon: "https://www.google.com/s2/favicons?domain=presstv.ir&sz=32",
    },
]

// ── India ────────────────────────────────────────────────────────────────────
export const indiaHlsSources: NewsSource[] = [
    {
        name: "NDTV 24x7",
        url: "/api/stream/proxy?url=https://ndtv24x7elemarchana.akamaized.net/hls/live/2003678-b/ndtv24x7/masterb_720p@3.m3u8",
        type: "hls",
        region: "India",
        icon: "https://www.google.com/s2/favicons?domain=ndtv.com&sz=32",
        epgId: "NDTV24x7.in"
    },
    {
        name: "India Today",
        url: "/api/stream/proxy?url=https://indiatodaylive.akamaized.net/hls/live/2014320/indiatoday/indiatodaylive/live_720p/chunks.m3u8",
        type: "hls",
        region: "India",
        icon: "https://www.google.com/s2/favicons?domain=indiatoday.in&sz=32",
        epgId: "IndiaToday.in"
    },
    {
        name: "CNN-News18",
        url: "/api/stream/proxy?url=https://n18syndication.akamaized.net/bpk-tv/CNN_News18_NW18_MOB/output01/index.m3u8",
        type: "hls",
        region: "India",
        icon: "https://www.google.com/s2/favicons?domain=news18.com&sz=32",
        epgId: "463909"
    },
    {
        name: "WION",
        url: "/api/stream/proxy?url=https://d7x8z4yuq42qn.cloudfront.net/index_1.m3u8",
        type: "hls",
        region: "India",
        icon: "https://www.google.com/s2/favicons?domain=wionews.com&sz=32",
        epgId: "463973"
    },
]

// ── Singapore ────────────────────────────────────────────────────────────────
export const singaporeHlsSources: NewsSource[] = [
    {
        name: "CNA",
        url: "/api/stream/proxy?url=https://d2e1asnsl7br7b.cloudfront.net/7782e205e72f43aeb4a48ec97f66ebbe/index.m3u8",
        type: "hls",
        region: "Singapore",
        icon: "https://www.google.com/s2/favicons?domain=channelnewsasia.com&sz=32",
        epgId: "CNA.sg"
    },
]

// ── China ────────────────────────────────────────────────────────────────────
export const chinaHlsSources: NewsSource[] = [
    {
        name: "CCTV+",
        url: "/api/stream/proxy?url=https://cd-live-stream.news.cctvplus.com/live/smil:CHANNEL2.smil/playlist.m3u8",
        type: "hls",
        region: "China",
        icon: "https://www.google.com/s2/favicons?domain=cctvplus.com&sz=32",
        epgId: "494985"
    },
    {
        name: "CGTN",
        url: "/api/stream/proxy?url=https://english-livebkali.cgtn.com/live/encgtn_0.m3u8",
        type: "hls",
        region: "China",
        icon: "https://www.google.com/s2/favicons?domain=cgtn.com&sz=32",
        epgId: "CGTNCCTVNews.us"
    },
]

// ── South Korea ──────────────────────────────────────────────────────────────
export const southKoreaHlsSources: NewsSource[] = [
    {
        name: "Arirang",
        url: "/api/stream/proxy?url=https://dash3.antik.sk/live/test_arirang/playlist.m3u8",
        type: "hls",
        region: "South Korea",
        icon: "https://www.google.com/s2/favicons?domain=arirang.com&sz=32",
        epgId: "ArirangTV.kr"
    },
]

// ── Japan ────────────────────────────────────────────────────────────────────
export const japanHlsSources: NewsSource[] = [
    {
        name: "NHK",
        url: "/api/stream/proxy?url=https://masterpl.hls.nhkworld.jp/hls/w/live/smarttv.m3u8",
        type: "hls",
        region: "Japan",
        icon: "https://www.google.com/s2/favicons?domain=nhk.or.jp&sz=32",
        epgId: "412179"
    },
]

// ── Australia ────────────────────────────────────────────────────────────────
export const australiaHlsSources: NewsSource[] = [
    {
        name: "ABC",
        url: "/api/stream/proxy?url=https://abc-news-dmd-streams-1.akamaized.net/out/v1/701126012d044971b3fa89406a440133/index.m3u8",
        type: "hls",
        region: "Australia",
        icon: "https://www.google.com/s2/favicons?domain=abc.net.au&sz=32",
        epgId: "3474"
    },
    {
        name: "Ticker",
        url: "/api/stream/proxy?url=https://live-hls-7agy.livepush.io/live_abr_cdn/nsitWAl1pTZtwca/emnmZbKZcO8RfwJR/index.m3u8",
        type: "hls",
        region: "Australia",
        icon: "https://www.google.com/s2/favicons?domain=tickernews.co&sz=32",
    },
]

// ── All HLS sources combined ──────────────────────────────────────────────────
export const allHlsSources: NewsSource[] = [
    ...usHlsSources,
    ...canadaHlsSources,
    ...businessHlsSources,
    ...ukHlsSources,
    ...europeHlsSources,
    ...middleEastHlsSources,
    ...indiaHlsSources,
    ...singaporeHlsSources,
    ...chinaHlsSources,
    ...southKoreaHlsSources,
    ...japanHlsSources,
    ...australiaHlsSources,
]
